import { Icon } from '@natera/material/lib/icon'
import { Collapsible } from 'radix-ui'
import { useEffect, useRef } from 'react'
import { cn } from '~/shared/lib/cn'
import { formatDateDefault } from '~/shared/lib/datetime'
import { Text } from '~/shared/ui'
import { useReleaseNotesSelectors } from '../../model'
import { ReleaseNoteBody } from './release-note-body'
import './release-note-item.scss'

export function ReleaseNoteItem({
  defaultOpen,
  title,
  version,
  body,
  isFirst,
  releaseDate,
}: {
  defaultOpen: boolean
  title: string
  version: string
  body: string
  isFirst: boolean
  releaseDate?: string
}) {
  const { lastReadReleaseNoteVersion, setLastReadReleaseNoteVersion } = useReleaseNotesSelectors()
  const isUnread = lastReadReleaseNoteVersion ? version > lastReadReleaseNoteVersion : true
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  const handleMouseEnter = () => {
    if (isUnread) {
      hoverTimeoutRef.current = setTimeout(() => {
        setLastReadReleaseNoteVersion(version)
      }, 2000)
    }
  }

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
  }

  return (
    <Collapsible.Root
      defaultOpen={defaultOpen}
      className="bg-primary-hovered rounded-lg"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Collapsible.Trigger className="group flex w-full cursor-pointer items-center justify-between gap-4 border-none bg-transparent px-4 py-3 text-left">
        <div className="relative flex w-full items-center justify-between gap-4">
          {isFirst && isUnread && (
            <span
              className={cn('bg-primary-main absolute top-[9px] -left-7 size-2 rounded-full', {
                'top-4.5': !!releaseDate,
              })}
            />
          )}
          <div className="flex flex-col py-1">
            <Text
              className="text-text-primary"
              appearance="SUBTITLE2"
            >
              {title}
            </Text>
            <Text
              className="text-text-secondary"
              appearance="CAPTION"
            >
              {releaseDate ? formatDateDefault(releaseDate) : ''}
            </Text>
          </div>
          <Text
            className="text-text-secondary"
            appearance="BODY"
          >
            v.{version}
          </Text>
        </div>
        <Icon
          children="keyboard_arrow_down"
          className="text-action-active text-[24px] group-data-[state=open]:rotate-180"
        />
      </Collapsible.Trigger>
      <Collapsible.Content className="c-release-note-item__content overflow-hidden px-4">
        <ReleaseNoteBody content={body} />
        {/* Next div is a hack for smooth animation (there's a glitchy animation when the content has bottom padding) */}
        <div className="pb-3" />
      </Collapsible.Content>
    </Collapsible.Root>
  )
}
